import { useState } from 'react'
import { Plus, Pencil, Trash2, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useTags, useCreateTag, useUpdateTag, useDeleteTag, useTagStatistics } from '@/hooks/useTags'
import { TagBadge } from './TagBadge'
import { TAG_COLORS } from '@/lib/validations/tag'
import type { Tag, TagCreateInput, TagUpdateInput } from '@/types/database'
import { useAnalytics } from '@/hooks/useAnalytics'

interface TagManagerProps {
  projectId: string
  canCreate?: boolean
  canEdit?: boolean
  canDelete?: boolean
}

/**
 * TagManager - Simplified tag management interface
 *
 * Features:
 * - Display standard tags (read-only)
 * - Create, edit, delete custom tags
 * - Color picker with predefined palette
 * - Usage statistics per tag
 */
export function TagManager({ projectId, canCreate = true, canEdit = true, canDelete = true }: TagManagerProps) {
  const { data: tags, isLoading } = useTags(projectId, true, 'name')
  const { data: statistics } = useTagStatistics(projectId)
  const createTagMutation = useCreateTag()
  const updateTagMutation = useUpdateTag()
  const deleteTagMutation = useDeleteTag()
  const { track } = useAnalytics()

  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingTag, setEditingTag] = useState<Tag | null>(null)

  // Form state
  const [formData, setFormData] = useState<{
    name: string
    display_name: string
    color: string
    description: string
  }>({
    name: '',
    display_name: '',
    color: TAG_COLORS[0].value,
    description: '',
  })

  // Separate standard and custom tags
  const standardTags = tags?.filter((t) => t.is_standard) || []
  const customTags = tags?.filter((t) => !t.is_standard) || []

  // Get usage count for a tag
  const getUsageCount = (tagName: string) => {
    return statistics?.find((s) => s.tag_name === tagName)?.clip_count || 0
  }

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      display_name: '#',
      color: TAG_COLORS[0].value,
      description: '',
    })
    setShowCreateForm(false)
    setEditingTag(null)
  }

  // Handle create tag
  const handleOpenCreateForm = () => {
    resetForm()
    setShowCreateForm(true)
  }

  // Handle edit tag
  const handleOpenEditForm = (tag: Tag) => {
    setEditingTag(tag)
    setFormData({
      name: tag.name,
      display_name: tag.display_name,
      color: tag.color,
      description: tag.description || '',
    })
    setShowCreateForm(false)
  }

  // Handle save tag
  const handleSave = async () => {
    try {
      if (editingTag) {
        // Update existing tag
        const updateData: TagUpdateInput = {
          display_name: formData.display_name,
          color: formData.color,
          description: formData.description || undefined,
        }
        await updateTagMutation.mutateAsync({ tagId: editingTag.id, data: updateData, projectId })

        // Track tag edited (no tag_name for privacy - may contain spoilers)
        track('tag_edited', {
          project_id: projectId,
          tag_id: editingTag.id,
        })
      } else {
        // Create new tag
        const createData: TagCreateInput = {
          name: formData.name,
          display_name: formData.display_name,
          color: formData.color,
          description: formData.description || undefined,
        }
        await createTagMutation.mutateAsync({ projectId, data: createData })

        // Track tag created (no tag_name for privacy - may contain spoilers)
        track('tag_created', {
          project_id: projectId,
          color: formData.color,
        })
      }
      resetForm()
    } catch (error) {
      console.error('Failed to save tag:', error)
    }
  }

  // Handle delete tag
  const handleDelete = async (tag: Tag) => {
    if (window.confirm(`Delete tag "${tag.display_name}"? This tag is used by ${getUsageCount(tag.name)} clips.`)) {
      try {
        await deleteTagMutation.mutateAsync({ tagId: tag.id, projectId })

        // Track tag deleted (no tag_name for privacy - may contain spoilers)
        track('tag_deleted', {
          project_id: projectId,
          tag_id: tag.id,
          usage_count: getUsageCount(tag.name),
        })
      } catch (error) {
        console.error('Failed to delete tag:', error)
      }
    }
  }

  // Auto-generate display name and convert name to valid format
  const handleNameChange = (value: string) => {
    // Convert to lowercase and replace spaces with underscores
    const convertedName = value.toLowerCase().replace(/\s+/g, '_')

    setFormData((prev) => ({
      ...prev,
      name: convertedName,
      display_name: `#${value}`, // Keep original for display
    }))
  }

  if (isLoading) {
    return <div className="flex items-center justify-center py-8">Loading tags...</div>
  }

  return (
    <div className="space-y-6">
      {/* Standard Tags Section */}
      <Card>
        <CardHeader>
          <CardTitle>Standard Tags</CardTitle>
          <CardDescription>Global tags available to all projects (read-only)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {standardTags.map((tag) => (
              <div key={tag.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-3">
                  <TagBadge tag={tag} size="md" />
                  {tag.description && <span className="text-sm text-muted-foreground">{tag.description}</span>}
                </div>
                <div className="text-sm text-muted-foreground">
                  {getUsageCount(tag.name)} clip{getUsageCount(tag.name) !== 1 ? 's' : ''}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Custom Tags Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div>
            <CardTitle>Custom Tags</CardTitle>
            <CardDescription>
              {canCreate || canEdit || canDelete
                ? 'Project-specific tags that you can create and manage'
                : 'Project-specific tags (read-only)'}
            </CardDescription>
          </div>
          {canCreate && (
            <Button onClick={handleOpenCreateForm} disabled={showCreateForm || editingTag !== null}>
              <Plus className="h-4 w-4 mr-2" />
              Create Tag
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Create/Edit Form */}
          {(showCreateForm || editingTag) && (
            <div className="border rounded-lg p-4 space-y-4 bg-muted/30">
              <h3 className="font-semibold">{editingTag ? 'Edit Tag' : 'Create New Tag'}</h3>

              {!editingTag && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Tag Name</label>
                  <input
                    type="text"
                    placeholder="e.g., Hero Performance"
                    value={formData.display_name.replace('#', '')}
                    onChange={(e) => handleNameChange(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                  {formData.name && (
                    <p className="text-xs text-muted-foreground">
                      Will be saved as: <span className="font-mono font-medium">{formData.name}</span>
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Spaces will be converted to underscores automatically
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <label className="text-sm font-medium">Display Name</label>
                <input
                  type="text"
                  placeholder="#Hero_Performance"
                  value={formData.display_name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, display_name: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Color</label>
                <div className="grid grid-cols-6 gap-2">
                  {TAG_COLORS.map((colorOption) => (
                    <button
                      key={colorOption.value}
                      type="button"
                      className={`h-10 rounded border-2 transition-all ${
                        formData.color === colorOption.value ? 'border-foreground scale-110' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: colorOption.value }}
                      onClick={() => setFormData((prev) => ({ ...prev, color: colorOption.value }))}
                      title={colorOption.name}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <label className="text-xs">Custom:</label>
                  <input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData((prev) => ({ ...prev, color: e.target.value }))}
                    className="h-8 w-20"
                  />
                  <span className="text-xs text-muted-foreground">{formData.color}</span>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Description (Optional)</label>
                <textarea
                  placeholder="Describe when to use this tag"
                  value={formData.description}
                  onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>

              {/* Preview */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Preview</label>
                <div className="flex items-center gap-2 p-3 border rounded-lg bg-background">
                  <TagBadge
                    tag={{
                      id: '',
                      project_id: projectId,
                      name: formData.name,
                      display_name: formData.display_name || '#Tag',
                      color: formData.color,
                      description: formData.description,
                      is_standard: false,
                      usage_count: 0,
                      created_at: '',
                      updated_at: '',
                    }}
                    size="md"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={!formData.name || !formData.display_name || !formData.color}
                >
                  {editingTag ? 'Update' : 'Create'}
                </Button>
              </div>
            </div>
          )}

          {/* Custom Tags List */}
          {customTags.length === 0 && !showCreateForm && !editingTag ? (
            <div className="text-center py-8 text-muted-foreground">
              <AlertCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No custom tags yet. Create your first tag to categorize clips for this project.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {customTags.map((tag) => (
                <div key={tag.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3 flex-1">
                    <TagBadge tag={tag} size="md" />
                    {tag.description && <span className="text-sm text-muted-foreground">{tag.description}</span>}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-sm text-muted-foreground">
                      {getUsageCount(tag.name)} clip{getUsageCount(tag.name) !== 1 ? 's' : ''}
                    </div>
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenEditForm(tag)}
                        disabled={editingTag !== null || showCreateForm}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                    {canDelete && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(tag)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
